// src/utils/formatters.ts

/**
 * Format numbers with thousands separators for display
 * @param num - Number to format
 * @returns Formatted number string
 */
export const formatNumber = (num: number): string => {
  if (num === 0) return "0";
  if (num < 1000) return num.toString();

  // For thousands, show K (e.g., 1.2K)
  if (num < 1000000) {
    const thousands = num / 1000;
    return thousands % 1 === 0 ? `${thousands}K` : `${thousands.toFixed(1)}K`;
  }

  // For millions, show M (e.g., 1.2M)
  const millions = num / 1000000;
  return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`;
};

/**
 * Format numbers with locale-specific thousands separators
 * @param num - Number to format
 * @param locale - Locale string (default: 'en-US')
 * @returns Formatted number string with commas
 */
export const formatNumberWithCommas = (
  num: number,
  locale: string = "en-US"
): string => {
  return new Intl.NumberFormat(locale).format(num);
};

/**
 * Format currency in Myanmar Kyat
 * @param amount - Amount to format
 * @param showSymbol - Whether to show currency symbol (default: true)
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number,
  showSymbol: boolean = true
): string => {
  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

  return showSymbol ? `${formatted} MMK` : formatted;
};

/**
 * Format currency in Lakhs (Myanmar property pricing)
 * @param lakhs - Amount in lakhs
 * @returns Formatted currency string
 */
export const formatLakhs = (lakhs: number): string => {
  if (lakhs === 0) return "0 Lakhs";
  if (lakhs < 1) return `${(lakhs * 100).toFixed(0)} Thousand`;

  const formatted = lakhs % 1 === 0 ? lakhs.toString() : lakhs.toFixed(1);
  return `${formatted} Lakhs`;
};

/**
 * Format percentage values
 * @param value - Percentage value
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number,
  decimals: number = 1
): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Format date strings
 * @param date - Date string or Date object
 * @param format - Format type ('short', 'long', 'medium')
 * @returns Formatted date string
 */
export const formatDate = (
  date: string | Date | null | undefined,
  format: "short" | "long" | "medium" = "medium"
): string => {
  if (!date) {
    return "N/A";
  }
  
  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (!dateObj || isNaN(dateObj.getTime())) {
    return "Invalid Date";
  }

  let options: Intl.DateTimeFormatOptions;

  switch (format) {
    case "short":
      options = {
        year: "2-digit",
        month: "numeric",
        day: "numeric",
      };
      break;
    case "long":
      options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        weekday: "long",
      };
      break;
    case "medium":
    default:
      options = {
        year: "numeric",
        month: "short",
        day: "numeric",
      };
      break;
  }

  return dateObj.toLocaleDateString("en-US", options);
};

/**
 * Format date and time
 * @param date - Date string or Date object
 * @returns Formatted datetime string
 */
export const formatDateTime = (date: string | Date): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return "Invalid Date";
  }

  return dateObj.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Get relative time from now (e.g., "2 hours ago")
 * @param date - Date string or Date object
 * @returns Relative time string
 */
export const getRelativeTime = (date: string | Date): string => {
  const now = new Date();
  const targetDate = typeof date === "string" ? new Date(date) : date;

  if (isNaN(targetDate.getTime())) {
    return "Invalid Date";
  }

  const diffInSeconds = Math.floor(
    (now.getTime() - targetDate.getTime()) / 1000
  );

  if (diffInSeconds < 60) return "Just now";
  if (diffInSeconds < 3600)
    return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400)
    return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000)
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 31536000)
    return `${Math.floor(diffInSeconds / 2592000)} months ago`;

  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
};

/**
 * Format file size in human readable format
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Format phone numbers (Myanmar format)
 * @param phone - Phone number string
 * @returns Formatted phone number
 */
export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, "");

  // Myanmar phone number format: +95 9XX XXX XXXX
  if (cleaned.length === 11 && cleaned.startsWith("95")) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 3)} ${cleaned.slice(
      3,
      6
    )} ${cleaned.slice(6)}`;
  }

  // Local format: 09XX XXX XXXX
  if (cleaned.length === 11 && cleaned.startsWith("09")) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  }

  // If format doesn't match, return as is
  return phone;
};

/**
 * Format property area (square feet)
 * @param sqft - Area in square feet
 * @returns Formatted area string
 */
export const formatArea = (sqft: number): string => {
  if (sqft < 1000) {
    return `${sqft} sq ft`;
  }

  const thousands = sqft / 1000;
  return `${thousands.toFixed(1)}k sq ft`;
};

/**
 * Format property dimensions
 * @param width - Width value
 * @param height - Height value
 * @param unit - Unit of measurement (default: 'ft')
 * @returns Formatted dimensions string
 */
export const formatDimensions = (
  width: number,
  height: number,
  unit: string = "ft"
): string => {
  return `${width} × ${height} ${unit}`;
};

/**
 * Truncate text with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

/**
 * Format address for display
 * @param address - Address components
 * @returns Formatted address string
 */
export const formatAddress = (address: {
  street?: string;
  township?: string;
  region?: string;
  country?: string;
}): string => {
  const parts = [
    address.street,
    address.township,
    address.region,
    address.country,
  ].filter(Boolean);

  return parts.join(", ");
};

/**
 * Convert English numbers to Myanmar numbers
 * @param num - Number to convert
 * @returns Myanmar number string
 */
export const toMyanmarNumbers = (num: string | number): string => {
  const myanmarDigits = ["၀", "၁", "၂", "၃", "၄", "၅", "၆", "၇", "၈", "၉"];
  return num.toString().replace(/\d/g, (digit) => {
    const index = parseInt(digit);
    return myanmarDigits[index] ?? digit;
  });
};

/**
 * Format status text with proper casing
 * @param status - Status string
 * @returns Formatted status string
 */
export const formatStatus = (status: string): string => {
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};
